// ============================================================================
// ConfigPanel — volet CONFIGURATION de l'onglet Remuneration (AD-D2)
// ============================================================================
// Porte du PoC avec TOKENS DU PARC. Etat React (P-ADM-14). Configs ADMIN-WIDE
// (routes /api/admin/remuneration/*, garde canAccessGestAdmin). Pourcentages :
// l'UI saisit des POINTS, l'API recoit des FRACTIONS.

import { useRef, useState } from 'react';
import { GsSelect, QueryError, SkeletonCard, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../../i18n';
import type { AdminMember, AdminMemberTeam } from '../../../hooks/useAdminMembers';
import {
  useAdminRemunerationConfig,
  remunerationConfigErrorCode,
} from '../../../hooks/useAdminRemunerationConfig';
import { buildConfigYearOptions } from '../../../lib/admin/config-year-options';
import { SectionHeader } from '../SectionHeader';
import { ConfigSummaryBar } from './ConfigSummaryBar';
import { EvalConfigSection } from './EvalConfigSection';
import { PayrollTargetsSection } from './PayrollTargetsSection';
import { PendingEchelonsSection } from './PendingEchelonsSection';
import { SeeAlsoLinks } from './SeeAlsoLinks';
import { YearCostSection } from './YearCostSection';

type SuccessAction = 'costs' | 'targets' | 'eval' | 'echelon';
type Banner =
  | { kind: 'error'; code: string }
  | { kind: 'success'; action: SuccessAction }
  | { kind: 'info' }
  | null;
type FocusSection = 'cost' | 'targets' | null;

function successLabel(action: SuccessAction, t: (key: string) => string): string {
  switch (action) {
    case 'costs':
      return t('gest:admin.remuneration.config.success.costs');
    case 'targets':
      return t('gest:admin.remuneration.config.success.targets');
    case 'eval':
      return t('gest:admin.remuneration.config.success.eval');
    case 'echelon':
      return t('gest:admin.remuneration.config.success.echelon');
  }
}

export function RemunerationConfigPanel({
  members,
  teams,
}: {
  members: AdminMember[];
  teams: AdminMemberTeam[];
}) {
  const { t } = useTranslation();
  const q = useAdminRemunerationConfig(true);
  const [banner, setBanner] = useState<Banner>(null);
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [focusSection, setFocusSection] = useState<FocusSection>(null);
  const costInputRef = useRef<HTMLInputElement>(null);
  const targetsInputRef = useRef<HTMLInputElement>(null);

  const onError = (err: unknown) => setBanner({ kind: 'error', code: remunerationConfigErrorCode(err) });
  const onClear = () => setBanner(null);
  const onSuccess = (action: SuccessAction) => setBanner({ kind: 'success', action });

  if (q.isLoading) {
    return <SkeletonCard />;
  }
  if (q.isError || !q.data) {
    return (
      <QueryError
        message={t('gest:admin.remuneration.config.loadError')}
        onRetry={() => void q.refetch()}
      />
    );
  }

  const d = q.data;
  const confirmableCount = d.pending.filter((p) => !p.offGrid).length;
  const currentYear = new Date().getFullYear();
  const yearOptions = buildConfigYearOptions(d.yearConfigs, d.teamTargets, currentYear);
  const yearNum = Number(selectedYear);
  const yearConfig = d.yearConfigs.find((row) => row.year === yearNum);
  const teamsTargeted = d.teamTargets.filter((row) => row.year === yearNum).length;
  const evalKey = `eval-${d.evalConfig?.passThreshold ?? 'n'}-${d.evalConfig?.maxScore ?? 'n'}-${d.evalConfig?.criteria.length ?? 0}`;

  function changeYear(value: string) {
    setSelectedYear(value);
    setFocusSection(null);
    setBanner(null);
  }

  function editYear(year: number, section: 'cost' | 'targets') {
    const next = String(year);
    setBanner(null);
    if (next === selectedYear) {
      const node = section === 'cost' ? costInputRef.current : targetsInputRef.current;
      node?.focus();
      return;
    }
    setFocusSection(section);
    setSelectedYear(next);
  }

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-6 p-5`}>
      <SectionHeader
        title={t('gest:admin.remuneration.config.title')}
        description={t('gest:admin.remuneration.config.description')}
      />

      <ConfigSummaryBar
        pendingCount={confirmableCount}
        vehicleCost={yearConfig?.vehicleCostBiweekly ?? null}
        revenuePct={yearConfig?.revenuePct ?? null}
        teamsTargeted={teamsTargeted}
        teamsTotal={teams.length}
      />

      {banner?.kind === 'error' ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          <span>{t(`gest:admin.remuneration.config.errors.${banner.code}`)}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            className="text-chip font-medium underline touch-manipulation"
          >
            {t('gest:management.common.dismiss')}
          </button>
        </div>
      ) : null}

      {banner?.kind === 'success' ? (
        <div
          role="status"
          className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-field text-green-700 dark:border-green-900 dark:bg-green-950/40 dark:text-green-300"
        >
          <span>{successLabel(banner.action, t)}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            className="text-chip font-medium underline touch-manipulation"
          >
            {t('gest:management.common.dismiss')}
          </button>
        </div>
      ) : null}

      {banner?.kind === 'info' ? (
        <div
          role="status"
          className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-field text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300"
        >
          <span>{t('gest:admin.remuneration.config.pending.notApplied')}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            className="text-chip font-medium underline touch-manipulation"
          >
            {t('gest:management.common.dismiss')}
          </button>
        </div>
      ) : null}

      <PendingEchelonsSection
        pending={d.pending}
        maxScore={d.evalConfig?.maxScore ?? null}
        members={members}
        onError={onError}
        onClear={onClear}
        onSuccess={() => onSuccess('echelon')}
        onUnchanged={() => setBanner({ kind: 'info' })}
      />

      <div className="flex flex-col gap-1.5 sm:max-w-xs">
        <span className="text-meta font-medium text-slate-600 dark:text-slate-300">
          {t('gest:admin.remuneration.config.year.label')}
        </span>
        <GsSelect
          value={selectedYear}
          onChange={changeYear}
          options={yearOptions.map((y) => ({ value: String(y), label: String(y) }))}
          ariaLabel={t('gest:admin.remuneration.config.year.label')}
          className="w-40 touch-manipulation"
        />
      </div>

      <YearCostSection
        key={`cost-${selectedYear}`}
        year={selectedYear}
        yearConfigs={d.yearConfigs}
        autoFocus={focusSection === 'cost'}
        inputRef={costInputRef}
        onError={onError}
        onClear={onClear}
        onSuccess={() => onSuccess('costs')}
        onEditYear={(y) => editYear(y, 'cost')}
      />
      <PayrollTargetsSection
        key={`targets-${selectedYear}`}
        year={selectedYear}
        yearConfigs={d.yearConfigs}
        teamTargets={d.teamTargets}
        teams={teams}
        autoFocus={focusSection === 'targets'}
        inputRef={targetsInputRef}
        onError={onError}
        onClear={onClear}
        onSuccess={() => onSuccess('targets')}
        onEditYear={(y) => editYear(y, 'targets')}
      />
      <EvalConfigSection
        key={evalKey}
        evalConfig={d.evalConfig}
        onError={onError}
        onClear={onClear}
        onSuccess={() => onSuccess('eval')}
      />
      <SeeAlsoLinks />
    </section>
  );
}
